'use client';

/**
 * PinnedCardShell — the peach header chrome around a "Pinned by Sara" card
 * (agenda / reminder cards pinned into the conversation). The white body
 * card itself is supplied by the consumer via `children`; this component
 * only owns the header row + dismiss affordance + outer surface.
 *
 * `compact` (design turn 14a) is the mobile-density variant: tighter
 * padding/radius/bg/border for the ~150px anatomy, and room in the header
 * row for `headerNav` (the prev/dots/next cluster — PinHeaderNav) between
 * the badge and the dismiss button. Added here rather than forking the
 * shell into a second component (the brief's explicit instruction) — the
 * tokens are the SAME V3_COLORS.pinned* values, just retuned, and desktop's
 * `compact` omission keeps its exact pre-turn-14 chrome.
 */
import type { ReactNode } from 'react';
import { Flag, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { V3_COLORS, V3_SHADOWS } from '@/lib/design-tokens';

export interface PinnedCardShellProps {
  onDismiss: () => void;
  children: ReactNode;
  className?: string;
  /** Mobile 14a compact anatomy — tighter shell padding/radius/bg/border.
   *  Defaults to false (desktop's original chrome, unchanged). */
  compact?: boolean;
  /** Badge label override. Defaults to "Pinned by Sara" — the mobile stack
   *  (14c) swaps this to the front pin's kind once history holds 2+ pins. */
  badgeLabel?: ReactNode;
  /** Prev/dots/next cluster (PinHeaderNav), rendered between the badge and
   *  the dismiss button — only passed when the pin holds >1 item. */
  headerNav?: ReactNode;
}

export function PinnedCardShell({
  onDismiss,
  children,
  className,
  compact = false,
  badgeLabel = 'Pinned by Sara',
  headerNav,
}: PinnedCardShellProps) {
  return (
    <div
      className={cn(
        'border-[1px]',
        compact ? 'rounded-[16px]' : 'rounded-[18px]',
        className
      )}
      style={{
        // The compact (mobile) design calls for rgba(255,246,236,.94) /
        // #F3D9BE — close enough to the existing pinnedBg/pinnedBorder
        // tokens (.9 alpha vs .94; #F3D9BE is an exact match) that reusing
        // them outright is the brief's own instruction, rather than adding
        // near-duplicate one-off literals for a few hundredths of opacity.
        backgroundColor: V3_COLORS.pinnedBg,
        borderColor: V3_COLORS.pinnedBorder,
        padding: compact ? '9px 11px 11px' : '12px 13px 14px',
        boxShadow: V3_SHADOWS.pinnedCard,
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <div
          className="flex shrink-0 items-center gap-1.5 font-['Work_Sans'] text-[11px] font-bold"
          style={{ color: V3_COLORS.orange, letterSpacing: '0.02em' }}
        >
          <Flag
            width={13}
            height={13}
            aria-hidden="true"
            className="shrink-0"
          />
          {badgeLabel}
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2">
          {headerNav}
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss pinned card"
            className="flex size-6 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-black/5"
            style={{ color: '#b79a7e' }}
          >
            <X width={compact ? 13 : 14} height={compact ? 13 : 14} />
          </button>
        </div>
      </div>
      <div className={compact ? 'mt-2' : 'mt-2.5'}>{children}</div>
    </div>
  );
}
